/**
 * ValoraaBead phone/tablet menu (snippets/vb-mobile-menu.liquid).
 * [data-vb-menu-toggle] opens the <dialog data-vb-menu-drawer> as a modal: the rest of the page
 * is inert, focus stays in the sheet, Escape closes it (native), and focus returns to the
 * toggle. aria-expanded follows the state, the page behind does not scroll, a tap on the
 * backdrop (tablet) closes it, and the country selector submits on change.
 * Delegated from the document so it keeps working after the theme editor re-renders the header.
 */
(() => {
  if (window.vbMobileMenu) return;
  window.vbMobileMenu = true;

  /** @param {HTMLDialogElement} drawer */
  const toggleFor = (drawer) => document.querySelector(`[data-vb-menu-toggle][aria-controls="${drawer.id}"]`);

  /** @param {HTMLDialogElement} drawer */
  const open = (drawer) => {
    if (drawer.open) return;
    drawer.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    toggleFor(drawer)?.setAttribute('aria-expanded', 'true');
    const close = drawer.querySelector('[data-vb-menu-close]');
    if (close instanceof HTMLElement) close.focus({ preventScroll: true });
    drawer.querySelector('.vb-msheet__sheet')?.scrollTo(0, 0);
  };

  /** @param {HTMLDialogElement} drawer */
  const onClosed = (drawer) => {
    document.documentElement.removeAttribute('scroll-lock');
    const toggle = toggleFor(drawer);
    toggle?.setAttribute('aria-expanded', 'false');
    if (toggle instanceof HTMLElement) toggle.focus({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const toggle = target.closest('[data-vb-menu-toggle]');
    if (toggle) {
      const drawer = document.getElementById(toggle.getAttribute('aria-controls') || '');
      if (drawer instanceof HTMLDialogElement) {
        event.preventDefault();
        open(drawer);
      }
      return;
    }

    const closeButton = target.closest('[data-vb-menu-close]');
    const drawer = target.closest('[data-vb-menu-drawer]');
    if (closeButton && drawer instanceof HTMLDialogElement) {
      drawer.close();
      return;
    }

    // A tap on the backdrop (outside the sheet) closes the menu
    if (target instanceof HTMLDialogElement && target.matches('[data-vb-menu-drawer]')) {
      const sheet = target.querySelector('.vb-msheet__sheet');
      const rect = sheet?.getBoundingClientRect();
      const e = /** @type {MouseEvent} */ (event);
      if (rect && (e.clientX > rect.right || e.clientX < rect.left)) target.close();
    }
  });

  // `close` does not bubble: listen in the capture phase
  document.addEventListener(
    'close',
    (event) => {
      const drawer = event.target;
      if (drawer instanceof HTMLDialogElement && drawer.matches('[data-vb-menu-drawer]')) onClosed(drawer);
    },
    true
  );

  // Desktop width: the sheet is not used there (unless the header is in its compact layout)
  window.matchMedia('(min-width: 990px)').addEventListener('change', (event) => {
    if (!event.matches) return;
    for (const drawer of document.querySelectorAll('dialog[data-vb-menu-drawer][open]')) {
      if (drawer.closest('[data-vb-compact]')) continue;
      /** @type {HTMLDialogElement} */ (drawer).close();
    }
  });

  // Country / currency selector submits on change (a <noscript> button covers no-JS)
  document.addEventListener('change', (event) => {
    const select = event.target;
    if (select instanceof HTMLSelectElement && select.matches('[data-vb-autosubmit]')) select.form?.submit();
  });
})();
